import type { ComponentProps, ElementType, ReactNode } from "react";
import { cn } from "@/lib/utils";

const sizes = {
  xs: "max-w-screen-sm", // 640px
  sm: "max-w-3xl", // 768px
  md: "max-w-5xl", // 1024px
  lg: "max-w-7xl", // 1280px (default)
  xl: "max-w-screen-xl", // 1280px
  full: "max-w-none", // no max-width
} as const;

type Size = keyof typeof sizes;

type GridContainerProps<T extends ElementType = "div"> = {
  children: ReactNode;
  className?: string;
  as?: T;
  size?: Size;
  grid?: boolean;
} & Omit<ComponentProps<T>, "as" | "children" | "className" | "size">;

const GridContainer = <T extends ElementType = "div">({
  children,
  className,
  as,
  size = "lg",
  grid = true,
  ...rest
}: GridContainerProps<T>) => {
  const Component = as || "div";

  return (
    <Component
      className={cn(
        // Container
        sizes[size],
        "w-full mx-auto px-4 md:px-8",
        // Grid (optional)
        grid && "grid grid-cols-12 gap-4 md:gap-6",
        className
      )}
      {...rest}
    >
      {children}
    </Component>
  );
};

export { GridContainer, sizes };
export type { Size, GridContainerProps };
